<template>
  <div class="bookmarks-page">
    <div class="page-header">
      <h1 class="page-title">관심 공고</h1>
      <p class="page-subtitle">찜한 공고 {{ bookmarkedJobs.length }}개</p>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
    </div>

    <div v-else-if="bookmarkedJobs.length === 0" class="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" stroke-width="1.5">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
      <p>아직 관심 공고가 없어요</p>
      <router-link to="/" class="btn-primary">공고 둘러보기</router-link>
    </div>

    <div v-else class="job-grid">
      <router-link 
        v-for="job in bookmarkedJobs" 
        :key="job.id" 
        :to="`/jobs/${job.id}`" 
        class="job-card"
      >
        <div class="card-thumbnail">
          <img :src="job.thumbnail || 'https://picsum.photos/640/360?random=' + job.id" :alt="job.siteName" />
          <span v-if="job.isPremium" class="premium-badge">PREMIUM</span>
          <button class="bookmark-btn active" @click.prevent="handleRemoveBookmark(job.id)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
        </div>
        <div class="card-content">
          <div class="card-tags">
            <span class="tag">{{ job.region }}</span>
            <span class="tag">{{ job.industry }}</span>
          </div>
          <h3 class="card-title">{{ job.siteName }}</h3>
          <p class="card-job-type">{{ job.jobType }}</p>
          <div class="card-salary">{{ job.salaryType }} {{ job.salary }}</div>
        </div>
      </router-link>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useJobs } from '@/composables/useJobs'
import { useBookmarks } from '@/composables/useBookmarks'

export default {
  name: 'MyBookmarksView',
  setup() {
    const { user, userProfile } = useAuth()
    const { getJob } = useJobs()
    const { removeBookmark, getBookmarks } = useBookmarks()

    const bookmarkedJobs = ref([])
    const loading = ref(true)

    const fetchBookmarkedJobs = async () => {
      if (!userProfile.value) {
        loading.value = false
        return
      }

      loading.value = true
      const bookmarkIds = getBookmarks(userProfile.value)
      
      const jobs = []
      for (const jobId of bookmarkIds) {
        try {
          const job = await getJob(jobId)
          if (job) {
            jobs.push(job)
          }
        } catch (e) {
          console.error('공고 로드 실패:', jobId, e)
        }
      }
      
      bookmarkedJobs.value = jobs
      loading.value = false
    }

    const handleRemoveBookmark = async (jobId) => {
      if (!user.value) return
      
      try {
        await removeBookmark(user.value.uid, jobId)
        bookmarkedJobs.value = bookmarkedJobs.value.filter(job => job.id !== jobId)
        userProfile.value.bookmarks = userProfile.value.bookmarks.filter(id => id !== jobId)
      } catch (e) {
        console.error('북마크 제거 실패:', e)
      }
    }

    onMounted(() => {
      if (userProfile.value) {
        fetchBookmarkedJobs()
      }
    })

    watch(userProfile, (newVal) => {
      if (newVal) {
        fetchBookmarkedJobs()
      }
    })

    return {
      bookmarkedJobs,
      loading,
      handleRemoveBookmark
    }
  }
}
</script>

<style scoped>
.bookmarks-page {
  max-width: 100%;
  padding: 0 24px 80px;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #191f28;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 15px;
  color: #6b7684;
  margin: 0;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f2f4f6;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-state p {
  font-size: 16px;
  color: #6b7684;
  margin: 20px 0 24px;
}

.btn-primary {
  padding: 14px 28px;
  background: #3182f6;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border-radius: 10px;
}

.job-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.job-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.job-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
}

.card-thumbnail {
  position: relative;
  padding-bottom: 56.25%;
  background: #f2f4f6;
}

.card-thumbnail img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.premium-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 8px;
  background: #3182f6;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
}

.bookmark-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  color: #e03131;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.bookmark-btn:hover {
  transform: scale(1.1);
}

.card-content {
  padding: 16px;
}

.card-tags {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.tag {
  padding: 4px 8px;
  background: #f2f4f6;
  color: #6b7684;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-job-type {
  font-size: 14px;
  color: #6b7684;
  margin-bottom: 12px;
}

.card-salary {
  font-size: 15px;
  font-weight: 700;
  color: #3182f6;
}

/* 대형 화면 */
@media (max-width: 1200px) {
  .job-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* 태블릿 */
@media (max-width: 768px) {
  .bookmarks-page {
    padding: 0 16px 60px;
  }

  .page-header {
    margin-bottom: 20px;
  }

  .page-title {
    font-size: 22px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .job-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .job-card {
    border-radius: 12px;
  }

  .card-content {
    padding: 14px;
  }

  .card-title {
    font-size: 15px;
  }

  .card-job-type {
    font-size: 13px;
    margin-bottom: 10px;
  }

  .card-salary {
    font-size: 14px;
  }

  .empty-state {
    padding: 60px 20px;
  }

  .loading-state {
    padding: 60px 0;
  }
}

/* 모바일 */
@media (max-width: 480px) {
  .bookmarks-page {
    padding: 0 12px 40px;
  }

  .page-header {
    margin-bottom: 16px;
  }

  .page-title {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 13px;
  }

  .job-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .job-card {
    border-radius: 12px;
  }

  .card-thumbnail {
    padding-bottom: 50%;
  }

  .premium-badge {
    top: 10px;
    left: 10px;
    padding: 3px 6px;
    font-size: 10px;
  }

  .bookmark-btn {
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
  }

  .bookmark-btn svg {
    width: 16px;
    height: 16px;
  }

  .card-content {
    padding: 12px;
  }

  .tag {
    padding: 3px 6px;
    font-size: 11px;
  }

  .card-title {
    font-size: 14px;
  }

  .card-job-type {
    font-size: 12px;
    margin-bottom: 8px;
  }

  .card-salary {
    font-size: 13px;
  }

  .empty-state {
    padding: 40px 16px;
  }

  .empty-state svg {
    width: 48px;
    height: 48px;
  }

  .empty-state p {
    font-size: 14px;
    margin: 16px 0 20px;
  }

  .btn-primary {
    padding: 12px 24px;
    font-size: 14px;
  }

  .loading-state {
    padding: 40px 0;
  }

  .spinner {
    width: 32px;
    height: 32px;
  }
}

/* 아이폰 SE */
@media (max-width: 375px) {
  .page-title {
    font-size: 18px;
  }

  .card-title {
    font-size: 13px;
  }

  .card-salary {
    font-size: 12px;
  }
}

/* 터치 디바이스 */
@media (hover: none) {
  .job-card:hover {
    transform: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .bookmark-btn:hover {
    transform: none;
  }
}
</style>